import React, { useEffect, useState } from 'react'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import Papa from 'papaparse'
import { useSnackbar } from 'notistack'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { styled, useTheme } from '@mui/material/styles'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { saveMaterial } from 'service/materials'
import isEqual from 'lodash.isequal'

export const MaterialUploader = ({ materials, csvFile, onCancel, query }) => {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [canSave, setCanSave] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const [rowModesModel, setRowModesModel] = useState({})

  const StyledDialog = styled(Dialog)(() => [
    {
      '& .MuiPaper-root': {
        background: theme.palette.background.default,
        zIndex: 10000
      }
    }
  ])

  const COLUMNS = [
    { field: 'MaterialNumber', headerName: 'Material Number', flex: 1, hideable: false, disableColumnMenu: true },
    {
      field: 'PartNumber',
      headerName: 'Part Number',
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'MaterialDescription',
      headerName: 'Material Description',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'OpenArea',
      headerName: 'Open Area',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'Weight',
      headerName: 'Gross Weight',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'SafetyDepth',
      headerName: 'Safety Depth',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'Status',
      headerName: 'Status',
      type: 'actions',
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: false,
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const isLoading = rowModesModel[id]?.mode === 'loading'

        if (isLoading) {
          return [
            <Box
              key="loading"
              sx={{
                width: '14px'
              }}>
              <CircularProgress
                sx={{
                  width: '14px'
                }}
                size="small"
              />
            </Box>
          ]
        }

        return [
          <Typography variant="body1" key="status">
            {row.Status}
          </Typography>
        ]
      }
    }
  ]

  const onSaveClicked = async () => {
    setIsSaving(true)
    const promises = rows.map(async (row) => {
      setRowModesModel({
        ...rowModesModel,
        [row.MaterialNumber]: {
          mode: 'loading'
        }
      })

      await saveMaterial(row)
    })

    try {
      await Promise.all(promises)
      enqueueSnackbar({
        message: `Material${promises.length > 1 ? 's were' : ' was'} successfully saved`,
        variant: 'success'
      })
      onCancel()
      await query.refetch()
    } catch (e) {
      enqueueSnackbar({
        message: 'An error occured while saving material',
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }

  useEffect(() => {
    Papa.parse(csvFile, {
      skipEmptyLines: true,
      header: true,
      complete: (results) => {
        setRows(
          results.data.map((importedMat) => {
            const keys = Object.keys(importedMat).filter((key) => key.split(' ').length > 1)
            for (const key of keys) {
              if (key === 'Gross Weight') {
                importedMat['Weight'] = importedMat[key]
              } else {
                importedMat[key.replace(/\s/g, '')] = importedMat[key]
              }
              delete importedMat[key]
            }
            let material = materials.find(
              (m) => m.MaterialNumber.toLowerCase() === importedMat.MaterialNumber.toLowerCase()
            )

            const actualKeys = [
              'MaterialNumber',
              'MaterialDescription',
              'PartNumber',
              'OpenArea',
              'SafetyDepth',
              'Weight'
            ]

            const isNew = material ? true : false
            if (material) {
              const material_ = {}
              for (const key of actualKeys) {
                if (!material[key] && !importedMat[key]) {
                  material_[key] = importedMat[key]
                } else {
                  material_[key] = material[key]
                }
              }
              material = material_
            }
            setCanSave(!isNew || !isEqual(material, importedMat))

            return {
              ...importedMat,
              Status: !isNew ? 'Will be created' : !isEqual(material, importedMat) ? 'Will be updated' : 'No action'
            }
          })
        )
        setIsLoading(false)
      },
      error: (error) => {
        setIsLoading(false)
        enqueueSnackbar({
          message: `An error occurred while parsing selected file: Error: ${error.message}`,
          variant: 'error'
        })
      }
    })
  }, [csvFile, materials, setIsLoading])

  const processRowUpdate = async (newRow, oldRow) => {
    const isEqual = JSON.stringify(newRow) === JSON.stringify(oldRow)
    if (isEqual) {
      return oldRow
    }

    const updatedRow = { ...newRow, isNew: false }
    setRows(rows.map((row) => (row.MaterialNumber === newRow.MaterialNumber ? updatedRow : row)))

    return updatedRow
  }

  return (
    <StyledDialog fullWidth maxWidth="lg" open onCancel={onCancel}>
      <DialogTitle
        color={'secondary'}
        sx={{
          zIndex: 100000
        }}>
        Upload materials
      </DialogTitle>

      <DialogContent>
        <DataGrid
          loading={isLoading}
          showCellVerticalBorder={false}
          getRowId={(data) => {
            return data.MaterialNumber
          }}
          disableMultipleRowSelection
          disableColumnFilter
          disableDensitySelector
          disableColumnMenu
          disableColumnSelector
          rows={rows}
          editMode="row"
          columns={COLUMNS}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton'
            }
          }}
          processRowUpdate={processRowUpdate}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={isSaving} variant="outlined" color="secondary" onClick={() => onCancel?.(undefined)}>
          Cancel
        </Button>
        <LoadingButton
          loading={isSaving}
          onClick={onSaveClicked}
          variant="contained"
          color="secondary"
          disabled={isSaving || rows.length === 0 || !canSave}>
          Save
        </LoadingButton>
      </DialogActions>
    </StyledDialog>
  )
}
